import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Layout } from '@robinpowered/ui-kit';
import { useApolloContext, useAuthContext } from 'contexts';
import {
  useAccessToken,
  ShellAuthData,
  ShellPermissions,
  createShellComposerBridge,
  ShellComposerBridgeProvider,
} from '@robinpowered/dashboard-apps-common';
import { RobinPageLoader } from '@robinpowered/dashboard-apps-patterns';
import { config } from 'config';
import { apps, routeCheckers } from '../single-spa-config';
import { createAuthBridge } from '../lib/authBridge';
import { DownloadMobileAppModal } from 'components/DownloadMobileAppModal';
import { AuthErrorOverlay } from 'components/AuthErrorOverlay';
import { SearchOverlay } from 'components/SearchOverlay/SearchOverlay';
import { usePresenceNotifier } from 'hooks/usePresenceNotifier';
import { useImportMapReady } from 'hooks/useImportMapReady';
import { useProfileSidebarUrlState } from 'hooks/useProfileSidebarUrlState';
import { useSingleSpaAppTransition } from 'hooks/useSingleSpaAppTransition';
import {
  getWorkspaceOrgSlugFromPathname,
  isShellPathWithoutWorkspaceOrg,
  isWorkspaceOrgSetupPath,
} from 'utils/workspaceOrgSlugFromPath';
import { isUsingParentToken } from 'utils/isUsingParentToken';
import { NavSidebar } from 'components/NavSidebar/NavSidebar';
import { MicroFrontendErrorBoundary } from 'components/MicroFrontendErrorBoundary';
import { PeopleProfileSidebar } from 'components/PeopleProfileSidebar/PeopleProfileSidebar';
import { EventOverlayHost } from 'components/EventOverlayHost';
import { AgentChatSidebar } from 'components/AgentChatSidebar';
import { ReconnectAccountsHost } from 'components/ReconnectAccountsHost';
import { GlobalReconnectBanner } from 'components/GlobalReconnectBanner';

export const AppShell = (): JSX.Element => {
  const bridgeRef = useRef<ReturnType<typeof createAuthBridge> | null>(null);
  if (!bridgeRef.current) {
    bridgeRef.current = createAuthBridge();
  }
  const bridge = bridgeRef.current;

  // The single event-composer bridge for this shell session. Created once (like
  // the auth bridge) so it survives MFE remounts, then handed to every MFE via
  // customProps and to the shell's own tree via the provider below.
  const composerBridgeRef = useRef<ReturnType<
    typeof createShellComposerBridge
  > | null>(null);
  if (!composerBridgeRef.current) {
    composerBridgeRef.current = createShellComposerBridge();
  }
  const composerBridge = composerBridgeRef.current;

  const location = useLocation();
  const { orgSlug: orgSlugRouteParam } = useParams<{ orgSlug: string }>();
  const { tenantId } = useApolloContext();
  const { accessToken } = useAccessToken(config.sessionCookieName);
  const {
    loading: authLoading,
    error: authError,
    isUnauthenticated,
    currentUser,
    currentOrg,
    orgSlug: contextOrgSlug,
    setOrgSlug,
    userPermissions,
    currentOrgHasVMLicenses,
  } = useAuthContext();

  const workspaceOrgSlugFromUrl = getWorkspaceOrgSlugFromPathname(
    location.pathname
  );
  // When ?use_parent_token=true, the MFE (e.g. tickets in an iframe) handles
  // its own auth via a token postMessaged from the parent. The shell's
  // session cookie is often absent, so shell-side auth will never resolve —
  // skip the loader and error overlay and let the MFE render.
  const usingParentToken = isUsingParentToken();
  const shellSkipsWorkspaceAuthLoader =
    isShellPathWithoutWorkspaceOrg(location.pathname) ||
    isWorkspaceOrgSetupPath(location.pathname) ||
    usingParentToken;

  useEffect(() => {
    setOrgSlug(workspaceOrgSlugFromUrl);
  }, [workspaceOrgSlugFromUrl, setOrgSlug]);

  // Extract the primitive fields we actually need from Apollo objects so the
  // dependency arrays below contain only primitives — no deep comparison needed.
  const orgId = currentOrg?.id;
  const orgSlugValue = currentOrg?.slug;
  const orgName = currentOrg?.name;
  const userId = currentUser?.id;
  const userEmail = currentUser?.primaryEmail?.email;
  const userName = currentUser?.name;
  const userAvatar = currentUser?.avatar;
  const userDefaultLocation = currentUser?.defaultLocation;

  const {
    canManageAmenities,
    canViewAnalytics,
    canViewBilling,
    canViewCheckpoints,
    canManageDevices,
    canViewDevices,
    canManageOfficeAccess,
    canViewOfficeAccess,
    canManageGroups,
    canManagePlaces,
    canManageRoles,
    canApproveSpaceRequests,
    canListUsers,
    canManageUsers,
    canViewVisitTab,
    canViewDeliveriesTab,
    canViewWorkweek,
    canManageWorkplaceServices,
    canManageWorkPolicies,
    canManageScenarioPlanning,
    arePersonalCalendarsEnabled,
    isInsightsFeatureEnabled,
    hasPendingApprovalNotifications,
  } = userPermissions;

  /**
   * Auth identity is resolved once the user/org query has completed and
   * ApolloContext has rebuilt the client with the tenant-id header.
   * This is independent of permissions — apps can render as soon as they
   * know who the user is.
   */
  const authResolved =
    !authLoading && !!tenantId && !!currentOrg && !!currentUser;

  /**
   * Permissions resolve independently after their own query completes
   * successfully. The query is skipped until auth is resolved (it needs
   * the org/tenant), so this implies authResolved when true.
   */
  const permissionsResolved =
    authResolved && !userPermissions.isLoading && !userPermissions.error;

  /**
   * Derive the auth payload for the bridge. Null until auth identity is ready.
   * All deps are primitives so plain useMemo is sufficient.
   */
  const resolvedAuthData = useMemo<ShellAuthData | null>(() => {
    if (!authResolved || !orgId || !userId || !accessToken) return null;
    return {
      accessToken,
      currentOrg: {
        id: orgId,
        slug: orgSlugValue ?? null,
        name: orgName ?? null,
      },
      currentUser: {
        id: userId,
        email: userEmail ?? null,
        name: userName ?? null,
        avatar: userAvatar ?? null,
        defaultLocation: userDefaultLocation ?? null,
      },
    };
  }, [
    authResolved,
    accessToken,
    orgId,
    orgSlugValue,
    orgName,
    userId,
    userEmail,
    userName,
    userAvatar,
    userDefaultLocation,
  ]);

  /**
   * Derive the permissions payload for the bridge. Null until permissions
   * have resolved (which implies auth is also resolved).
   */
  const resolvedPermissions = useMemo<ShellPermissions | null>(() => {
    if (!permissionsResolved) return null;
    return {
      canManageAmenities,
      canViewAnalytics,
      canViewBilling,
      canViewCheckpoints,
      canManageDevices,
      canViewDevices,
      canManageOfficeAccess,
      canViewOfficeAccess,
      canManageGroups,
      canManagePlaces,
      canManageRoles,
      canApproveSpaceRequests,
      canListUsers,
      canManageUsers,
      canManageVisits: canViewVisitTab,
      canViewDeliveriesTab,
      canViewWorkweek,
      canManageWorkplaceServices,
      canManageWorkPolicies,
      canManageScenarioPlanning,
      arePersonalCalendarsEnabled,
      isInsightsFeatureEnabled,
      hasPendingApprovalNotifications,
      currentOrgHasVMLicenses,
    };
  }, [
    permissionsResolved,
    canManageAmenities,
    canViewAnalytics,
    canViewBilling,
    canViewCheckpoints,
    canManageDevices,
    canViewDevices,
    canManageOfficeAccess,
    canViewOfficeAccess,
    canManageGroups,
    canManagePlaces,
    canManageRoles,
    canApproveSpaceRequests,
    canListUsers,
    canManageUsers,
    canViewVisitTab,
    canViewDeliveriesTab,
    canViewWorkweek,
    canManageWorkplaceServices,
    canManageWorkPolicies,
    canManageScenarioPlanning,
    arePersonalCalendarsEnabled,
    isInsightsFeatureEnabled,
    hasPendingApprovalNotifications,
    currentOrgHasVMLicenses,
  ]);

  /**
   * Notify the bridge as soon as each piece of auth data is independently
   * available. Apps subscribe and decide for themselves what they need before
   * rendering — some may only require auth data, others may wait for both.
   */
  useEffect(() => {
    if (!resolvedAuthData) return;
    bridge.resolveAuthData(resolvedAuthData);
  }, [bridge, resolvedAuthData]);

  useEffect(() => {
    if (!resolvedPermissions) return;
    bridge.resolvePermissions(resolvedPermissions);
  }, [bridge, resolvedPermissions]);

  // Register single-spa apps as soon as the import map is ready.
  // Bundles begin loading immediately; auth data arrives via the bridge.
  useImportMapReady(bridge, composerBridge);

  // Track whether single-spa has mounted its first micro-frontend.
  // The shell overlay stays visible until this fires, preventing a blank
  // flash while single-spa asynchronously imports and mounts the app.
  const [appMounted, setAppMounted] = useState(false);
  // Track app-to-app transitions so the loader covers the gap where the
  // previous app has unmounted but the next has not yet mounted.
  const isAppTransitioning = useSingleSpaAppTransition();
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
  const openSearchOverlay = useCallback(() => {
    setIsSearchOverlayOpen(true);
  }, []);
  const closeSearchOverlay = useCallback(() => {
    setIsSearchOverlayOpen(false);
  }, []);
  // Toggle the search overlay with ⌘K (macOS) / Ctrl+K (Windows/Linux).
  // Esc-to-close is handled by Ant Design's Modal.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      const isModifier = event.metaKey || event.ctrlKey;
      if (isModifier && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOverlayOpen((isOpen) => !isOpen);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  useEffect(() => {
    const handler = () => setAppMounted(true);
    window.addEventListener('single-spa:first-mount', handler);
    return () => window.removeEventListener('single-spa:first-mount', handler);
  }, []);

  // Detect error states that would otherwise leave the user on an infinite spinner.
  // Auth redirect errors (expired/missing token) are handled by RootRouteTree,
  // so these cover network failures, invalid org slugs, and permissions errors.
  // Wait until AuthContext's orgSlug has synced to the URL before deciding the
  // org is missing. Between mount/navigation and the setOrgSlug effect firing,
  // the auth query is still skipped (authLoading=false) but currentOrg is null
  // — without this guard, that brief render flashes the "org not found" error
  // (e.g. on the redirect from /event/:eventID into /:orgSlug/schedule/meeting-rooms).
  const authContextOrgSlugSynced = contextOrgSlug === workspaceOrgSlugFromUrl;
  const isOrgNotFound =
    !!workspaceOrgSlugFromUrl &&
    authContextOrgSlugSynced &&
    !authLoading &&
    !authError &&
    !currentOrg &&
    !isWorkspaceOrgSetupPath(location.pathname);
  const hasQueryError =
    (!authLoading && !!authError && !isUnauthenticated) ||
    (!userPermissions.isLoading && !!userPermissions.error);
  const showError = !usingParentToken && (isOrgNotFound || hasQueryError);

  // @TODO: Should this be a prop passed from the child app up to the parent?
  const isFullScreen = useMemo(() => {
    // When embedded in an iframe (e.g. schedule app embedding tickets), hide the nav sidebar.
    // NOTE: this assumes that all embedded apps are full-screen. We will need a more
    // detailed check in the future if embedded apps need to show the nav sidebar.
    if (window.self !== window.top) {
      return true;
    }

    const isApplicationFullScreen = apps.find((app) =>
      app.activeWhen(window.location)
    )?.isFullScreen;
    const isRouteFullScreen = routeCheckers.some((checker) =>
      checker(location.pathname)
    );

    return isApplicationFullScreen || isRouteFullScreen;
  }, [location.pathname]);

  // Held in the URL so a refresh (or a shared link) restores the same person
  // in the same layout.
  const {
    userId: profileUserId,
    view: profileView,
    openProfile,
    closeProfile: closeUserProfile,
    setView: setProfileView,
  } = useProfileSidebarUrlState();
  const openUserProfile = useCallback(
    (userId: string) => {
      openProfile(userId);
      closeSearchOverlay();
    },
    [openProfile, closeSearchOverlay]
  );

  usePresenceNotifier({ activeOrgId: tenantId ?? null });

  return (
    <ShellComposerBridgeProvider composerBridge={composerBridge}>
      <Container>
        <DownloadMobileAppModal />
        <SearchOverlay
          open={isSearchOverlayOpen}
          onClose={closeSearchOverlay}
          onSelectUser={openUserProfile}
          onSelectMapResource={closeUserProfile}
        />

        {!isFullScreen && <NavSidebar onOpenSearch={openSearchOverlay} />}

        <MainContainer>
          <GlobalReconnectBanner />
          <ContentWrapper>
            <MicroFrontendErrorBoundary>
              {showError ? (
                <AuthErrorOverlay
                  orgSlug={orgSlugRouteParam}
                  isOrgNotFound={isOrgNotFound}
                  hasQueryError={hasQueryError}
                />
              ) : (
                <>
                  {!shellSkipsWorkspaceAuthLoader &&
                    (!authResolved || !appMounted || isAppTransitioning) && (
                      <LoadingOverlay>
                        <RobinPageLoader />
                      </LoadingOverlay>
                    )}
                  <SingleSpaRoot id="singleSpaRoot" />
                </>
              )}
            </MicroFrontendErrorBoundary>
          </ContentWrapper>
        </MainContainer>

        <EventOverlayHost />
        <ReconnectAccountsHost />

        <AgentChatSidebar authBridge={bridge} />

        <PeopleProfileSidebar
          onClose={closeUserProfile}
          userId={profileUserId ?? undefined}
          isSearchOpen={isSearchOverlayOpen}
          view={profileView}
          onViewChange={setProfileView}
        />
      </Container>
    </ShellComposerBridgeProvider>
  );
};

const { Content } = Layout;

const Container = styled(Layout)`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: row;
  height: 100vh;
`;

const MainContainer = styled(Content)`
  background: #f8f8f8;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
  height: 100%;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  z-index: 1;
`;

const SingleSpaRoot = styled.div`
  height: 100%;
`;
